import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from '@mui/icons-material';

import InputText from 'components/library/Input/InputText/InputText';
import InputPhone from 'components/library/Input/InputPhone/InputPhone';
import checkEmail from 'utils/checkEmail';

import styles from './InvestmentsForm.module.css';
import InvestmentsFormDropdown from './InvestmentsFormDropdown';
import { AccountState } from 'store/features/account/types';

type ErrorForm = {
  phone?: string;
  email?: string;
};

interface Props {
  account: AccountState,
  name: string,
  domain: string,
  project: number,
  errors: ErrorForm,
  stockPrice: number,
  isConnected: boolean,
  minimum: number,
  setPhoneError: (value: boolean) => void,
  setEmailError: (value: boolean) => void,
  isPEA: boolean,
  children: React.ReactNode,
  actions: {
    setAccountData: (value: any) => void
  },
}

export default function InvestmentsForm({
  account, actions, name, domain, project, errors,
  stockPrice, isConnected, minimum, children,
  setPhoneError, setEmailError, isPEA,
}: Props) {
  const { t } = useTranslation();

  useEffect(() => {
    if (window.sessionStorage.getItem('from_company') === 'true') {
      actions.setAccountData({ wantInvestBySociety: window.sessionStorage.getItem('from_company') });
    } else {
      actions.setAccountData({ wantInvestBySociety: false });
      window.sessionStorage.setItem('from_company', 'false');
    }
  }, [actions])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.name) {
      if (e.target.name === 'email') {
        setEmailError(!checkEmail(e.target.value));
      }
      actions.setAccountData({ [e.target.name]: e.target.value });
    } else {
      actions.setAccountData({ phone: e });
    }
  }

  const handleInvestByCompany = (e: React.ChangeEvent<HTMLInputElement>) => {
    const from_company = e.target.checked;
    actions.setAccountData({ wantInvestBySociety: from_company });
    window.sessionStorage.setItem('from_company', from_company.toString());
  }

  const getBackLink = () => {
    const token = new URL(window.location.href).searchParams.get('token');
    if (token && token !== 'null') {
      return `https://${domain}/company/land2/${project}/?token=${token}`;
    }
    const email = new URL(window.location.href).searchParams.get('email');
    if (email && email !== 'null') {
      return `https://${domain}/company/land2/${project}/?email=${email}`;
    }
    return `https://${domain}/company/land2/${project}`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <a
          className={styles.link}
          href={getBackLink()}
          data-cy="back-link"
        >
          <ArrowLeft className={styles.icon} />
          <span>
            {t('navigation.backTo')} {name || t('navigation.project')}
          </span>
        </a>
        <h1 className={styles.title}>
          {t('investments.title')}
        </h1>
        <form className={styles.form}>
          <InvestmentsFormDropdown
            value={account.investment}
            sharesPrice={stockPrice}
            minimum={minimum}
            setValue={(investment) => actions.setAccountData({ investment })}
            isPEA={isPEA}
          />
          <div className={styles.invest}>
            <input
              type="checkbox"
              className={styles.checkbox}
              onChange={handleInvestByCompany}
              checked={account.wantInvestBySociety}
              data-cy="invest-by-company"
            />
            <p>
              {t('account.investBySociety')}
            </p>
          </div>
          {
            (account.wantInvestBySociety)
            && (
              <p className={`fadeIn ${styles.hint}`}>
                {t('account.societyInformationLater')}
              </p>
            )
          }
          {
            (!isConnected)
            && (
              <>
                <p className={styles.subtitle}>
                  {t('investments.personal')}
                </p>
                <div className={styles.row}>
                  <InputText
                    label={t('account.firstname')}
                    name="firstname"
                    valueInput={account.firstname}
                    onTextChange={handleChange}
                  />
                  <InputText
                    label={t('account.lastname')}
                    name="lastname"
                    valueInput={account.lastname}
                    onTextChange={handleChange}
                  />
                </div>
                <InputText
                  label="Email"
                  name="email"
                  valueInput={account.email}
                  onTextChange={handleChange}
                  state={errors.email ? 'ERROR' : 'DEFAULT'}
                  hint={errors.email && t('account.email.invalid')}
                />
                <InputPhone
                  label={t('account.phone')}
                  phone={account.phone}
                  setPhone={handleChange}
                  setError={setPhoneError}
                />
              </>
            )
          }
          {children}
        </form>
      </div>
    </div>
  );
}
